// Shared derivations for the family-tree views that aren't the genealogy chart
// itself. The chart works in "units" (a couple drawn side by side) because it
// draws connectors; the timeline and the fan want plainer answers - which
// generation is this person in, when were they born, who are my ancestors -
// so those live here rather than being re-derived per renderer.

// A node's birth date, normalised across the three shapes the payload can
// carry it in:
//
//   owner / collaborator -> birthday: "1957-12-30", birth_year_known: true
//   public share         -> birth_year: 1957        (a year, never a date)
//
// `known` is false when there is no usable year at all. That matters because
// Contact::BIRTHDAY_NO_YEAR stores a day-and-month-only birthday as year 4:
// "0004-11-24" is a real 24 November with an unknown year, and plotting that
// at x = year 4 would put Felien two millennia to the left of everyone else.
// The server already applied the rule (Contact#birthday_year_known?), so trust
// birth_year_known rather than re-testing the year here.
export function birthOf(node) {
    if (node.birthday) {
        let date = new Date(`${node.birthday}T00:00:00`)
        if (Number.isNaN(date.getTime())) return { known: false }
        if (!node.birth_year_known) return { known: false, month: date.getMonth(), day: date.getDate() }
        return { known: true, date, year: date.getFullYear(), month: date.getMonth(), day: date.getDate() }
    }
    if (Number.isFinite(node.birth_year)) {
        // Year-only (the public share): anchor mid-year so a decade of
        // year-only people doesn't pile up on the 1 January gridline.
        return { known: true, date: new Date(node.birth_year, 6, 1), year: node.birth_year, yearOnly: true }
    }
    return { known: false }
}

// Age in whole years, or null when the year isn't known.
// A node's death date, in the same three shapes birthOf handles:
//
//   member / owner -> died_on: "2009-04-11", death_year_known: true
//   public share   -> death_year: 2009        (a year, never a date)
//
// `known` is false for somebody who IS deceased but whose date nobody has -
// which is a real and common state, not missing data to paper over. Callers
// have to say something honest about it rather than guess an endpoint.
export function deathOf(node) {
    if (!node.deceased) return { deceased: false, known: false }
    if (node.died_on && node.death_year_known) {
        let date = new Date(`${node.died_on}T00:00:00`)
        if (!Number.isNaN(date.getTime())) return { deceased: true, known: true, date, year: date.getFullYear() }
    }
    if (Number.isFinite(node.death_year)) {
        return { deceased: true, known: true, date: new Date(node.death_year, 6, 1), year: node.death_year, yearOnly: true }
    }
    return { deceased: true, known: false }
}

// Age at DEATH for somebody gone, current age for somebody living.
//
// Null when they are gone and nobody knows when: their age is genuinely
// unknown, and "104" - today minus a birth year - is a statement about a person
// who has not been alive for years.
export function ageOf(node, today = new Date()) {
    let birth = birthOf(node)
    if (!birth.known) return null

    let death = deathOf(node)
    if (death.deceased && !death.known) return null

    let at = death.known ? death.date : today
    let age = at.getFullYear() - birth.year
    let beforeBirthday = at.getMonth() < birth.month || (at.getMonth() === birth.month && at.getDate() < birth.day)
    if (birth.yearOnly || death.yearOnly) return age
    return beforeBirthday ? age - 1 : age
}

// "1922-2009", "1922-" for somebody gone whose date nobody has, or just the
// birth year for the living. Nil without a birth year to anchor it.
export function lifespanLabel(node) {
    let birth = birthOf(node)
    if (!birth.known) return null

    let death = deathOf(node)
    if (!death.deceased) return String(birth.year)
    return death.known ? `${birth.year}-${death.year}` : `${birth.year}-`
}

// Generation depth per node: 0 for anyone with no parent in the graph, then
// one more than the deepest parent. Partners are levelled to the same depth
// afterwards, so a couple always shares a lane even when only one of them has
// parents recorded (which is the normal case - you have your own parents in
// the tree, your partner's are somebody else's tree).
//
// Iterative rather than recursive, with a pass cap: the edge set is
// user-entered and a cycle (A parent of B, B parent of A) is only prevented
// per-pair by the model, not around a longer loop. A cycle must degrade to a
// slightly odd lane assignment, never a blown stack.
// Generations counted FROM a person: a parent is one up, a child one down, a
// partner the same row. The chart lays out by this, and the timeline bands by
// it, so "third generation" means the same set of people in both - it did not
// when each view counted for itself.
//
// Returns null when there is no ego to count from; callers fall back to
// `generations`, which counts depth from whatever has no parents recorded.
export function generationsFromEgo({ egoId: ego, parentsOf, childrenOf, partnersOf }) {
    if (ego == null) return null

    let gen = new Map([[ego, 0]])
    let queue = [ego]
    let guard = 0
    while (queue.length && guard++ < 8192) {
        let id = queue.shift()
        let here = gen.get(id)
        let step = (others, delta) => {
            (others || []).forEach((other) => {
                if (gen.has(other)) return

                gen.set(other, here + delta)
                queue.push(other)
            })
        }
        step(parentsOf?.get(id), -1)
        step(childrenOf?.get(id), 1)
        step(partnersOf?.get(id), 0)
    }
    return gen
}

export function generations({ nodes, parentsOf, partnersOf }) {
    let depth = new Map(nodes.map((node) => [node.id, 0]))
    let maxPasses = Math.min(nodes.length, 64)

    for (let pass = 0; pass < maxPasses; pass++) {
        let changed = false
        nodes.forEach((node) => {
            let parents = parentsOf.get(node.id) || []
            if (!parents.length) return
            let deepest = -1
            parents.forEach((pid) => {
                if (!depth.has(pid)) return
                deepest = Math.max(deepest, depth.get(pid))
            })
            if (deepest < 0) return
            if (depth.get(node.id) < deepest + 1) {
                depth.set(node.id, deepest + 1)
                changed = true
            }
        })
        if (!changed) break
    }

    // Level couples. One pass is enough in practice and bounded regardless.
    nodes.forEach((node) => {
        (partnersOf.get(node.id) || []).forEach((pid) => {
            if (!depth.has(pid)) return
            let level = Math.max(depth.get(node.id), depth.get(pid))
            depth.set(node.id, level)
            depth.set(pid, level)
        })
    })
    return depth
}

// The viewer's own node. Falls back to null, and callers treat that as "no
// ego, so no fan chart".
//
// `viewer` is checked FIRST and is the only reliable answer: on a share link
// addressed to somebody in the tree, the payload is read from their seat, so
// the owner's "self" node is still present but is no longer the person looking.
// Keying off "self" before that flag centred the fan on the sender instead of
// on the recipient the whole page had been relabelled for.
//
// The rest is the older shape: the owner payload calls it "self", the
// collaborator payload "owner", and both also carry a relation of "You" /
// "Tree owner".
export function egoId({ nodes, nodesById }) {
    let flagged = nodes.find((node) => node.viewer)
    if (flagged) return flagged.id
    // Nobody is reading this as themselves, but the chart still has a centre -
    // the tree's owner on an unaddressed share. Without it the fan has nothing
    // to radiate from and renders empty.
    let root = nodes.find((node) => node.root)
    if (root) return root.id
    if (nodesById.has("self")) return "self"
    if (nodesById.has("owner")) return "owner"
    let me = nodes.find((node) => node.relation === "You" || node.relation === "Tree owner")
    return me ? me.id : null
}

// Ancestor slots for a fan chart, in the classic Ahnentafel arrangement: ring
// 1 holds 2 slots, ring 2 holds 4, ring r holds 2^r. Slot i in a ring has its
// parents at 2i and 2i+1 in the next ring out.
//
// Returns every slot up to `rings`, INCLUDING the empty ones (node: null) -
// the gaps are the point. An empty great-grandparent arc is what tells you
// there is a generation still to be entered, which a genealogy chart renders
// as nothing at all.
//
// Where a person has two parents recorded, the order is arbitrary (the payload
// has no father/mother marker), so it's whatever the edge order gives.
export function ancestorSlots({ egoId: ego, parentsOf, nodesById, rings = 4 }) {
    if (!ego) return []
    let slots = []
    let byRing = new Map([[0, [ego]]])

    for (let ring = 1; ring <= rings; ring++) {
        let previous = byRing.get(ring - 1) || []
        let current = []
        for (let i = 0; i < Math.pow(2, ring - 1); i++) {
            let childId = previous[i]
            let parents = childId == null ? [] : (parentsOf.get(childId) || [])
            current.push(parents[0] ?? null, parents[1] ?? null)
        }
        byRing.set(ring, current)
        current.forEach((id, index) => {
            slots.push({ ring, index, total: current.length, node: id == null ? null : (nodesById.get(id) || null) })
        })
    }
    return slots
}

// One click, one meaning. Every view drew a person as a link only when the
// payload gave them a `url` - which a shared tree emits only for somebody the
// VIEWER owns a contact for. So half the people in a chart were inert, and
// which half depended on the reader's own address book rather than on anything
// visible. `detail_url` is present for everyone (see
// FamilyTrees::SharedPayload#node), and the detail sidebar picks it up off any
// element, SVG included.
export function personOpenable(node) {
    return !!(node.detail_url || node.url)
}

// Works for HTML and SVG anchors: SVG needs setAttribute for href, and both
// support dataset.
export function applyPersonLink(el, node) {
    // A detail-only person still needs a real, keyboard-operable link.
    // The delegated detail handler intercepts ordinary activation.
    if (node.url || node.detail_url) el.setAttribute("href", node.url || node.detail_url)
    if (node.detail_url) {
        el.dataset.detailUrl = node.detail_url
        el.dataset.turboFrame = "_top"
    }
}
